import { Box, Flex, Tab, TabList, TabPanel, TabPanels, Tabs, Text, theme, useBoolean } from '@chakra-ui/react';
import { ReactComponent as RandomIcon } from 'assets/icons/random.svg';
import { UPGRADE_REDIRECT_LINK } from 'constants/links';
import { PALETTE_APPLIED, UPGRADE } from 'constants/mixpanel';
import { Mixpanel } from 'libs/third-party/Mixpanel/mixpanel';
import { BrandOptionDropdown } from 'modules/Editor/components/BrandOptionDropdown';
import { StepColorPickerContainer } from 'modules/Editor/components/ColorPicker';
import { applySelectedColorPalette, useAppDispatch, useAppSelector } from 'modules/Editor/store';
import { selectBrandIdx } from 'modules/Editor/store/editorSettingsSelector';
import { PermissionBadge, UserTypes, useBrand, useUser } from 'modules/User';
import { memo, useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { setAnalyticsUpgradeFeatureProp } from 'utils/sessionStorage';

import { SIDE_MENU_WIDTH } from '../../WidgetMenu.config';
import { EditBrandKitButton, NoLogoView } from '../BrandKitMenu';
import { PUBLIC_COLOR_PALETTES } from './StylesMenu.config';
import { getBrandColorPalette } from './StylesMenu.helpers';

interface ColorPaletteProps {
  first: boolean;
  last: boolean;
  color: string;
  count: number;
}

export const ColorRect = memo(({ first, last, color, count }: Readonly<ColorPaletteProps>) => {
  return (
    <Box
      background={color}
      w={`${(SIDE_MENU_WIDTH - 32) / count}px`}
      h='34px'
      borderTopLeftRadius={first ? 'base' : 0}
      borderBottomLeftRadius={first ? 'base' : 0}
      borderTopRightRadius={last ? 'base' : 0}
      borderBottomRightRadius={last ? 'base' : 0}
    ></Box>
  );
});

ColorRect.displayName = 'ColorRect';

interface ColorPaletteSetProps {
  palette: string[];
  showBadge?: boolean;
  onShuffle: (colors: string[]) => void;
}

const ColorPaletteSet = memo(({ palette, showBadge, onShuffle }: ColorPaletteSetProps) => {
  const [isHovered, setIsHovered] = useBoolean(false);

  const handleClickPalette = useCallback(() => onShuffle(palette), [palette, onShuffle]);

  return (
    <StepColorPickerContainer
      display={'flex'}
      flexFlow={'row'}
      mx='auto'
      my='0.5rem'
      cursor={'pointer'}
      position='relative'
      onClick={handleClickPalette}
      onMouseEnter={setIsHovered.on}
      onMouseLeave={setIsHovered.off}
      onFocus={setIsHovered.on}
      onBlur={setIsHovered.off}
      as='button'
      aria-label='color palette set'
      data-testid='color-palette-set'
    >
      {showBadge && <PermissionBadge permission={'business'} position={'absolute'} top={-2} right={2} />}
      <Box
        position='absolute'
        color='#FFF'
        w='100%'
        display={showBadge ? 'none' : 'flex'}
        justifyContent='center'
        placeItems={'center'}
        pt='5px'
        pb='6px'
        borderRadius={'base'}
        background={isHovered ? 'rgba(0, 0, 0, 0.5)' : 'rgba(0, 0, 0, 0)'}
        transition='background .33s'
      >
        <Text
          fontWeight={'medium'}
          color={isHovered ? 'rgba(255, 255, 255, 1)' : 'rgba(255, 255, 255, 0)'}
          transition='color .33s'
        >
          Shuffle
        </Text>
        <RandomIcon
          color={isHovered ? 'rgba(255, 255, 255, 1)' : 'rgba(255, 255, 255, 0)'}
          style={{ transition: 'color .33s', marginLeft: theme.space[2] }}
        />
      </Box>
      {palette.map((c, subIndex) => {
        const isFirst = subIndex === 0;
        const isLast = subIndex === palette.length - 1;
        return <ColorRect first={isFirst} last={isLast} color={c} key={`color_${subIndex}`} count={palette.length} />;
      })}
    </StepColorPickerContainer>
  );
});

ColorPaletteSet.displayName = 'ColorPaletteSet';

interface ColorPaletteListProps {
  colorPaletteList: Array<string[]>;
  hasBrandKitPermission?: boolean;
  onShuffle: (colors: string[]) => void;
}

const ColorPaletteList = memo(({ colorPaletteList, hasBrandKitPermission, onShuffle }: ColorPaletteListProps) => {
  return (
    <Flex flexFlow={'column wrap'} marginInlineStart='unset'>
      {colorPaletteList.map((palette, index) => (
        <ColorPaletteSet
          key={`color_palettes_${index}`}
          palette={palette}
          showBadge={!hasBrandKitPermission}
          onShuffle={onShuffle}
        />
      ))}
    </Flex>
  );
});

ColorPaletteList.displayName = 'ColorPaletteList';

export const StylesMenu = (): JSX.Element => {
  const [focusTabPanel] = useState(false);
  const { isFreeUser, isPremiumUser, isEducationUser, isGuestRole } = useUser();
  const { t } = useTranslation('editor_widget_menu', { useSuspense: false });
  const dispatch = useAppDispatch();
  const brandIdx = useAppSelector(selectBrandIdx);
  const { data: brandData } = useBrand();

  const { brandConfig = { editable: false } } = useBrand({
    brandId: brandData?.[brandIdx]?.id,
  });

  // Free and Premium users should always not have the brand dropdown. other user types depends on the number of brands.
  const showBrandKitDropdown = brandData && brandData.length > 1;

  // Free and Premium users should not be able to use but see the logo's with business badge labeled.
  const hasBrandKitPermission = !isFreeUser && !isPremiumUser && !isGuestRole;

  // Free and Premium has the permission to edit their brand because they are the owner.
  // Enterprise/Business admin users should have the permission to edit their brand. However, Members and Guests are not.
  // Right now, E1 backend handle the permission check.
  const hasBrandEditPermission = isFreeUser || isPremiumUser || brandConfig.editable;

  const brandColorPalettes = useMemo(() => {
    return brandData && brandData.length > 0 ? getBrandColorPalette(brandData[brandIdx]) : [];
  }, [brandIdx, brandData]);

  const hasNoColorPalettes = brandData && brandColorPalettes.length > 0;

  const hasNoBrandKit = !brandData || (brandData && (brandColorPalettes.length === 0 || !brandColorPalettes));

  const handleShuffleColors = useCallback(
    (colors: string[], isBrandKit?: boolean) => {
      if (isBrandKit && !hasBrandKitPermission) {
        setAnalyticsUpgradeFeatureProp(UPGRADE.feature.BrandkitPalette);
        window.open(UPGRADE_REDIRECT_LINK, '_self');
        Mixpanel.track(UPGRADE.eventName, {
          feature: UPGRADE.feature.Brandkit,
          plan: UserTypes.Business,
          Feature: UPGRADE.feature.BrandkitPalette,
        });
        return;
      }

      dispatch(applySelectedColorPalette({ colors }));
      Mixpanel.track(PALETTE_APPLIED.eventName, {
        type: isBrandKit ? PALETTE_APPLIED.type.BrandKit : PALETTE_APPLIED.type.Generic,
      });
    },
    [hasBrandKitPermission, dispatch],
  );

  return (
    <Box>
      <Tabs style={{ flexFlow: 'column' }}>
        <TabList h='48px'>
          <Tab fontSize='sm' fontWeight='semibold' _focus={{ boxShadow: 'inset var(--vg-shadows-outline)' }}>
            {t('sideMenu.stylesMenu.explore')}
          </Tab>
          {!isEducationUser && (
            <Tab fontSize='sm' fontWeight='semibold' _focus={{ boxShadow: 'inset var(--vg-shadows-outline)' }}>
              {t('sideMenu.stylesMenu.brandKit')}
            </Tab>
          )}
        </TabList>
        <TabPanels overflowY={'auto'} height={'calc(100vh - 10rem)'}>
          <TabPanel
            style={{
              boxShadow: focusTabPanel ? 'inset var(--vg-shadows-outline)' : 'unset',
            }}
          >
            <Box>
              <ColorPaletteList
                colorPaletteList={PUBLIC_COLOR_PALETTES}
                hasBrandKitPermission={true}
                onShuffle={handleShuffleColors}
              />
            </Box>
          </TabPanel>
          <TabPanel
            style={{
              boxShadow: focusTabPanel ? 'inset var(--vg-shadows-outline)' : 'unset',
            }}
            h='100%'
          >
            <Box position='relative' h='full'>
              {showBrandKitDropdown && (
                <Flex alignItems='center' justifyContent='space-between' mb={theme.space[4]}>
                  <Text fontSize='sm' fontWeight='semibold'>
                    {t('sideMenu.stylesMenu.brandKit')}
                  </Text>
                  <BrandOptionDropdown w={56} triggerProps={{ h: 8 }} />
                </Flex>
              )}
              {hasNoColorPalettes && (
                <Box position={'relative'} h={'calc(var(--vg-sizes-full) - 3rem)'}>
                  <Box>
                    {brandData && (
                      <ColorPaletteList
                        colorPaletteList={brandColorPalettes}
                        hasBrandKitPermission={hasBrandKitPermission}
                        onShuffle={(colors) => handleShuffleColors(colors, true)}
                      />
                    )}
                  </Box>
                </Box>
              )}
              {hasBrandEditPermission && (
                <Box position={'absolute'} bottom={theme.space[2]} w={'100%'}>
                  <EditBrandKitButton marginX={'initial'} marginY={'initial'} w={'100%'} />
                </Box>
              )}
              {hasNoBrandKit && (
                <Flex h='100%'>
                  <NoLogoView
                    buttonLabel={t('sideMenu.stylesMenu.addColorsToBrandKit')}
                    hasBrandEditPermission={hasBrandEditPermission}
                  />
                </Flex>
              )}
            </Box>
          </TabPanel>
        </TabPanels>
      </Tabs>
    </Box>
  );
};

export default StylesMenu;
